<template>
  <b-card title="Add Test" class="test-form-card">
    <b-overlay :show="isSubmitting" rounded>
      <!-- Selection Controls -->
      <b-row class="mb-3">
        <b-col md="6" cols="12">
          <AutoComplete
            :label="$t('lab.test')"
            :placeholder="$t('lab.test')"
            :multiple="true"
            v-model="singleTests"
            param-query="search"
            :serializer="testSerializer"
            endpoint="test"
            :close-on-select="false"
            text-title="name"
            @input="addTestsToList"
          />
        </b-col>
        <b-col md="6" cols="12">
          <AutoComplete
            :label="$t('lab.test-group')"
            :placeholder="$t('lab.test-group')"
            v-model="group"
            param-query="search"
            :serializer="(val) => val.id"
            endpoint="group"
            text-title="name"
            @input="selectGroup"
          />
        </b-col>
        <b-col md="6">
          <b-form-group
            label="Sample ID"
            label-for="sample-id"
            label-class="font-weight-bold"
          >
            <b-form-input
              id="sample-id"
              v-model="sampleId"
              placeholder="Enter sample ID"
              trim
            ></b-form-input>
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group
            label="Sample Date"
            label-for="sample-date"
            label-class="font-weight-bold"
          >
            <b-form-datepicker
              id="sample-date"
              v-model="sampleDate"
              :max="new Date().toISOString().slice(0, 10)"
              placeholder="Select date"
              today-button
              reset-button
              close-button
              class="mb-2"
            ></b-form-datepicker>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Selected Tests Section -->
      <div class="selected-tests-section">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">{{ $t("lab.test") }}</h5>
          <b-button
            v-if="testListFiltered.length > 0"
            size="sm"
            variant="outline-danger"
            @click="clearAllTests"
          >
            <i class="fas fa-trash-alt mr-1"></i> Clear All
          </b-button>
        </div>

        <div class="selected-tests-container">
          <div v-if="!testListFiltered.length" class="text-center py-4">
            <i class="fas fa-vial text-muted mb-2" style="font-size: 2rem"></i>
            <p class="text-muted mb-0">
              No tests selected. Please select tests from the dropdown above.
            </p>
          </div>

          <b-table
            v-else
            :items="testListFiltered"
            :fields="testColumns"
            striped
            hover
            responsive
            show-empty
            empty-text="No tests selected"
            class="mb-0"
            thead-class="bg-light"
          >
            <template #cell(name)="data">
              <span class="font-weight-medium">{{ data.item.name }}</span>
            </template>

            <template #cell(price)="data">
              <b-badge variant="light" class="text-dark">
                {{ formatPrice(data.item.price) }}
              </b-badge>
            </template>

            <!-- <template #cell(actions)="data">
              <b-button
                @click="removeTest(data.index)"
                variant="outline-danger"
                size="sm"
                class="py-0"
              >
                <i class="fas fa-times"></i>
              </b-button>
            </template> -->
          </b-table>
        </div>

        <!-- Summary Section -->
        <div
          v-if="testListFiltered.length > 0"
          class="d-flex justify-content-between mt-2 p-2 bg-light rounded"
        >
          <span class="font-weight-bold"
            >Total Tests: {{ testListFiltered.length }}</span
          >
          <span class="font-weight-bold"
            >Total Price: {{ formatPrice(totalPrice) }}</span
          >
        </div>
      </div>

      <!-- Submit Button -->
      <div class="d-flex justify-content-end mt-3">
        <b-button variant="secondary" class="mr-2" @click="$router.go(-1)">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="addTest()"
          :disabled="testListFiltered.length === 0 || isSubmitting"
        >
          <b-spinner v-if="isSubmitting" small></b-spinner>
          <span>Submit</span>
        </b-button>
      </div>
    </b-overlay>
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BTooltip,
  BFormGroup,
  BSpinner,
  BFormCheckboxGroup,
  BFormCheckbox,
  BOverlay,
  BFormDatepicker,
} from "bootstrap-vue";
const AutoComplete = () => import("@/components/common/AutoComplete.vue");
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import Vue from "vue";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BTooltip,
    AutoComplete,
    BFormGroup,
    BSpinner,
    BFormCheckboxGroup,
    BFormCheckbox,
    BOverlay,
    BFormDatepicker,
  },

  data() {
    return {
      loading: false,
      isSubmitting: false,
      testsList: [],
      singleTests: [],
      group: null,
      sampleId: "",
      sampleDate: new Date().toISOString().slice(0, 10),
      lastSelectedGroupId: null,
      lastTestGroup: [],

      testColumns: [
        { key: "name", label: "Test Name" },
        { key: "price", label: "Price" },
        { key: "actions", label: "", class: "text-center" },
      ],
    };
  },

  computed: {
    testListFiltered() {
      // Create a completely new array with unique tests based on test_id
      const uniqueMap = new Map();

      // Process all tests and keep only the latest version of each test_id
      this.testsList.forEach((test) => {
        // Ensure we're using a consistent ID property
        const id = test.test_id || test.id;
        if (id) {
          uniqueMap.set(id, test);
        }
      });

      // Convert the Map values back to an array
      return Array.from(uniqueMap.values());
    },

    totalPrice() {
      return this.testListFiltered.reduce(
        (sum, test) => sum + Number(test.price || 0),
        0
      );
    },
  },
  watch: {
    singleTests: {
      handler(newVal, oldVal) {
        if (!oldVal) return;

        // If the new array is shorter than the old one, something was removed
        if (newVal.length < oldVal.length) {
          // Find which items were removed
          const newIds = newVal.map((item) => item.test_id || item.id);
          const removedItems = oldVal.filter(
            (item) => !newIds.includes(item.test_id || item.id)
          );

          // Remove those items from testsList
          removedItems.forEach((removedItem) => {
            this.testsList = this.testsList.filter(
              (test) => test.test_id !== (removedItem.test_id || removedItem.id)
            );
          });
        }
      },
      deep: true,
    },
  },
  methods: {
    testSerializer(val) {
      return {
        name: val.name,
        test_id: val.id,
        price: val.price,
      };
    },

    formatPrice(price) {
      return price ? `${Number(price).toLocaleString()} IQD` : "0 IQD";
    },

    clearTests() {
      this.singleTests = [];
      this.testsList = this.testsList.filter((test) => test.group_id);
    },

    clearAllTests() {
      this.testsList = [];
      this.singleTests = [];
      this.group = null;
      this.sampleId = "";
      this.sampleDate = "";
    },

    removeDuplicatesById(array) {
      const seen = new Map();
      return array.filter((item) => {
        if (!seen.has(item.test_id)) {
          seen.set(item.test_id, true);
          return true;
        }
        return false;
      });
    },

    removeTest(index) {
      const newTests = [...this.testsList];
      newTests.splice(index, 1);
      Vue.set(this, "testsList", newTests);
    },

    addTestsToList(e) {
      if (!e || !e.length) return;

      // Make a working copy of the tests list
      const newTestsList = [...this.testsList];

      // Create a map of existing test IDs
      const existingIds = new Set();
      newTestsList.forEach((test) => {
        const id = test.test_id || test.id;
        if (id) existingIds.add(id);
      });

      // Process and add new tests
      const testsToAdd = [];
      e.forEach((newTest) => {
        const testId = newTest.test_id || newTest.id;

        // Only add if not already in the list
        if (testId && !existingIds.has(testId)) {
          const test = {
            test_id: testId,
            name: newTest.name,
            price: newTest.price || 0,
          };
          testsToAdd.push(test);
          existingIds.add(testId);
        }
      });

      // Only update the list if we have new tests
      if (testsToAdd.length > 0) {
        this.testsList = [...newTestsList, ...testsToAdd];
      }
    },

    async selectGroup(e) {
      try {
        this.loading = true;

        // First, remove any tests from the previous group selection
        if (this.lastSelectedGroupId !== null) {
          this.testsList = this.testsList.filter(
            (test) => test.group_id !== this.lastSelectedGroupId
          );
        }

        // Store the new group ID
        this.lastSelectedGroupId = e;

        // Fetch the new group data
        await this.getPatient(`/testGroup?filter[group_id]=${e}&include=test`);
        const groupTests = this.all_data?.data || [];

        if (groupTests.length === 0) {
          // No tests in this group
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "No tests found in this group",
              variant: "warning",
            },
          });
        } else {
          // Map the new group tests
          const mappedTests = groupTests.map((test) => ({
            group_id: e, // Add group_id to identify these tests later
            price: test.test.price,
            test_id: test.test.id,
            name: test.test.name,
          }));

          // Create a map of existing test_ids for quick lookup
          const existingTestIds = new Set(
            this.testsList.map((test) => test.test_id)
          );

          // Filter out any tests that already exist in testsList
          const newGroupTests = mappedTests.filter(
            (newTest) => !existingTestIds.has(newTest.test_id)
          );

          // Add new group tests to the list
          if (newGroupTests.length > 0) {
            this.testsList = [...this.testsList, ...newGroupTests];
          }
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Error fetching test group",
            variant: "danger",
          },
        });
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async addTest() {
      if (this.testListFiltered.length === 0) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Please select at least one test",
            variant: "warning",
          },
        });
        return;
      }

      // if (!this.sampleId.trim()) {
      //   this.$toast({
      //     component: ToastificationContent,
      //     position: "top-right",
      //     props: {
      //       title: "Sample ID is required",
      //       variant: "warning",
      //     },
      //   });
      //   return;
      // }
      console.log(this.testListFiltered);
      try {
        this.isSubmitting = true;

        const finalData = {
          patient_card_id: this.$route.params.id,
          tests: this.testListFiltered.map((test) => {
            if (test.group_id) {
              delete test.group_id;
            }
            return test;
          }),
          sample_id: this.sampleId,
        };

        if (this.sampleDate) {
          finalData.date = this.sampleDate;
        }

        await this.$store.dispatch("create", {
          form: finalData,
          path: `/patientCardTest`,
        });

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Tests added successfully",
            variant: "success",
          },
        });

        this.$router.go(-1);
      } catch (error) {
        console.error(error);

        if (error.response?.status === 400) {
          const errors = error.response.data;
          for (let item in errors) {
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Validation Error",
                variant: "danger",
                text: errors[item][0],
              },
            });
          }
        } else {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error adding tests",
              variant: "danger",
            },
          });
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    handleTestRemoval(removedItem) {
      if (!removedItem) return;

      const removeId = removedItem.test_id || removedItem.id;
      if (removeId) {
        this.testsList = this.testsList.filter((test) => {
          const testId = test.test_id || test.id;
          return testId !== removeId;
        });
      }
    },
  },
};
</script>

<style scoped>
.test-form-card {
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
}

.selected-tests-section {
  margin-bottom: 1.5rem;
}

.selected-tests-container {
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 1rem;
  background-color: #f8f9fa;
  min-height: 200px;
}

/deep/ .table td {
  vertical-align: middle;
  padding: 0.5rem;
}

/deep/ .table thead th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.8rem;
  letter-spacing: 0.5px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
